import React, { FC, useEffect, useState } from "react";
import CustomTable from "../../../components/Tables/CustomTable";
import { MoreVertical } from "lucide-react";
import DropdownMenu from "../../ui/DropdownMenu";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import moment from "moment";
import { cleanObject } from "../../../helpers";
import { fetchBuyersRequestsAsync } from "../../../redux/features/requestManagementSlice";

interface RequestsTableProps {
  status: string;
  search: string;
  filterByCategory: string;
  startDate: Date | null;
  endDate: Date | null;
}

const RequestsTable: FC<RequestsTableProps> = ({
  status,
  search,
  filterByCategory,
  startDate,
  endDate,
}) => {
  const { user }: any = useAppSelector((state) => state.auth);

  const [page, setPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);
  const { buyersRequests, loading } = useAppSelector((state) => state.requests);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const columns = [
    { label: "S/N", field: "index" },
    { label: "Product Description", field: "productDescription" },
    { label: "Quantity", field: "quantity" },
    { label: "Category", field: "categories" },
    { label: "Buyer Name", field: "buyerName" },
    { label: "Buyer Email", field: "buyerEmail" },
    { label: "Status", field: "status" },
    { label: "Created At", field: "createdAt" },
    { label: "Expires At", field: "expiresAt" },
    { label: "Action", field: "action" },
  ];

  const dropdownOptions = [
    user?.user?.roles !== "INVESTOR" && {
      label: "View request details",
      onClick: (id: string) =>
        navigate(`/request-management/request-details/${id}`),
      returnBy: "id",
    },
  ].filter(Boolean);

  const requestData = buyersRequests?.data?.map((req: any, index: number) => ({
    index: page * pageLimit + index + 1,
    productDescription: req?.productDescription,
    quantity: req?.quantity,
    categories: req?.categories,
    buyerName: req?.buyer?.fullName,
    buyerEmail: req?.buyer?.email,
    status: (
      <span
        className={`text-xs px-3 py-1 rounded-md font-semibold ${
          req.status === "INPROGRESS"
            ? "bg-blue-50 text-blue-500 border border-blue-500"
            : req.status === "COMPLETED"
            ? "bg-green-50 text-green-500 border border-green-500"
            : req.status === "PENDING"
            ? "bg-yellow-50 text-yellow-500 border border-yellow-500"
            : req.status === "EXPIRED"
            ? "bg-gray-100 text-gray-500 border border-gray-400"
            : req.status === "CANCELLED"
            ? "bg-red-50 text-red-500 border border-red-500"
            : "bg-muted text-muted-foreground border border-muted"
        }`}
      >
        {req.status?.toUpperCase()}
      </span>
    ),
    createdAt: moment(req?.createdAt).format("Do MMM YYYY"),
    expiresAt: moment(req?.expiresAt).format("Do MMM YYYY"),
    action: (
      <DropdownMenu
        options={dropdownOptions}
        icon={<MoreVertical style={{ cursor: "pointer" }} />}
        item={req}
      />
    ),
  }));

  useEffect(() => {
    const params = cleanObject({
      limit: pageLimit,
      page: page + 1,
      filter: search,
      category: filterByCategory,
      startDate,
      endDate,
      status,
    });

    dispatch(fetchBuyersRequestsAsync(params));
  }, [
    dispatch,
    page,
    pageLimit,
    search,
    filterByCategory,
    startDate,
    endDate,
    status,
  ]);

  return (
    <CustomTable
      data={{
        columns,
        rows: requestData,
      }}
      noRecordText="No Requests Available"
      loading={loading}
      showFooter={true}
      totalPages={buyersRequests?.meta?.totalPages}
      itemsPerPage={buyersRequests?.meta?.limit}
      currentPage={page}
      onPageChange={(value: number) => {
        setPage(value);
      }}
      onLimitChange={(value: number) => {
        setPageLimit(value);
        setPage(0);
      }}
    />
  );
};

export default RequestsTable;
